// DAS-2160: "Your News Trend" modal for PortfolioAnalysisView
// Shows TrendSearch Exposure (winners/losers/enablers at ticker level)
// and Sector Exposure by TrendSearch Sentiment (positive/neutral/negative).
// Clickable chips open detail dialogs. Works with both Redux and file-drop routes.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import api from '../../functions/api';
import getUserId from '../../functions/getUserId';
import ClickableTickerChip from './ClickableTickerChip';

const PAModal_NewsTrend = ({
  data = [],
  source = 'portfolio',
  exposures = [],
  tickerAggregates: parentAggregates = [],
  sectorSentiments: parentSentiments = [],
}) => {
  const [tickerAggregates, setTickerAggregates] = useState(parentAggregates);
  const [sectorSentiments, setSectorSentiments] = useState(parentSentiments);
  const [sectorExposureData, setSectorExposureData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [detailTitle, setDetailTitle] = useState('');
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const needsAggregates = !parentAggregates.length;
    const needsSentiments = !parentSentiments.length;

    const promises = [];
    if (needsAggregates) promises.push(api.get('/trendsearch_ticker_aggregates'));
    else promises.push(Promise.resolve(null));
    if (needsSentiments) promises.push(api.get('/trendsearch_sector_summary'));
    else promises.push(Promise.resolve(null));

    if (source === 'portfolio') {
      const userId = getUserId();
      promises.push(api.get('/pf_other_exposures', { params: { userid: userId } }));
    } else {
      promises.push(Promise.resolve(null));
    }

    setLoading(true);
    Promise.all(promises)
      .then(([tickerRes, sectorRes, exposureRes]) => {
        if (tickerRes?.data?.success) setTickerAggregates(tickerRes.data.data || []);
        if (sectorRes?.data?.success) setSectorSentiments(sectorRes.data.data || []);
        if (exposureRes?.data?.portfolio_various_exposures_filtered) {
          setSectorExposureData(exposureRes.data.portfolio_various_exposures_filtered);
        }
      })
      .catch(err => console.error('[PAModal_NewsTrend] fetch error:', err))
      .finally(() => setLoading(false));
  }, [source, parentAggregates.length, parentSentiments.length]);

  const holdings = useMemo(() => {
    if (exposures.length > 0) {
      return exposures.map(x => ({
        Ticker: (x.ticker || '').toUpperCase(),
        Company: x.company || '',
        Value_eur: parseFloat(x.total_amount_exposure || x.stock_exposure || 0) || 0,
      }));
    }
    return data.filter(d => d.Ticker).map(d => ({
      Ticker: (d.Ticker || '').toUpperCase(),
      Company: d.Company || '',
      Value_eur: parseFloat(d.Value_eur) || parseFloat(d.exposure_pct) || 0,
    }));
  }, [exposures, data]);

  const aggByTicker = useMemo(() => {
    const map = Object.create(null);
    tickerAggregates.forEach(row => {
      map[(row.ticker || '').toUpperCase()] = row;
    });
    return map;
  }, [tickerAggregates]);

  const trendExposure = useMemo(() => {
    if (!holdings.length || !tickerAggregates.length) return null;

    let totalValue = 0, winnerValue = 0, loserValue = 0, enablerValue = 0;

    holdings.forEach(h => {
      const val = h.Value_eur;
      totalValue += val;
      const agg = aggByTicker[h.Ticker];
      if (!agg) return;
      const w = parseInt(agg.winner_count || 0, 10);
      const l = parseInt(agg.loser_count || 0, 10);
      const e = parseInt(agg.enabler_count || 0, 10);
      if (w > l && w >= e) winnerValue += val;
      else if (l > w && l >= e) loserValue += val;
      else enablerValue += val;
    });

    const pct = (x) => totalValue > 0 ? (x / totalValue * 100) : 0;
    return { totalValue, winnerValue, loserValue, enablerValue, winnerPct: pct(winnerValue), loserPct: pct(loserValue), enablerPct: pct(enablerValue) };
  }, [holdings, tickerAggregates, aggByTicker]);

  const openTickerDetail = (kind) => {
    if (!trendExposure) return;
    const rows = [];
    holdings.forEach(h => {
      const agg = aggByTicker[h.Ticker];
      if (!agg) return;
      const w = parseInt(agg.winner_count || 0, 10);
      const l = parseInt(agg.loser_count || 0, 10);
      const e = parseInt(agg.enabler_count || 0, 10);
      let label = 'enabler';
      if (w > l && w >= e) label = 'winner';
      else if (l > w && l >= e) label = 'loser';
      if (label !== kind) return;
      const pct = trendExposure.totalValue > 0 ? (h.Value_eur / trendExposure.totalValue * 100) : 0;
      rows.push({ ticker: h.Ticker, company: h.Company, sector: agg.sector || '', value: h.Value_eur, pct });
    });
    rows.sort((a, b) => b.value - a.value);
    const titleMap = { winner: 'Winners', loser: 'Losers', enabler: 'Enablers/Neutral' };
    setDetailRows(rows);
    setDetailTitle(`TrendSearch Exposure \u2014 ${titleMap[kind]}`);
    setDetailOpen(true);
  };

  const sentimentBySector = useMemo(() => {
    const map = Object.create(null);
    sectorSentiments.forEach(row => {
      const name = (row.sector || '').trim();
      if (name) map[name] = parseFloat(row.overall_sentiment || 0);
    });
    return map;
  }, [sectorSentiments]);

  const sectorExposure = useMemo(() => {
    if (!sectorSentiments.length) return null;

    let sectorExposures;

    if (source === 'portfolio' && sectorExposureData?.sector) {
      sectorExposures = sectorExposureData.sector.map(s => ({
        sector: (s.name || '').trim(),
        exposurePct: parseFloat(s.exposure_perc || 0),
      }));
    } else if (holdings.length && tickerAggregates.length) {
      const totalVal = holdings.reduce((s, h) => s + h.Value_eur, 0);
      const sectorAgg = {};
      holdings.forEach(h => {
        const agg = aggByTicker[h.Ticker];
        if (!agg || !agg.sector) return;
        const sector = agg.sector.trim();
        sectorAgg[sector] = (sectorAgg[sector] || 0) + h.Value_eur;
      });
      sectorExposures = Object.entries(sectorAgg).map(([sector, val]) => ({
        sector,
        exposurePct: totalVal > 0 ? (val / totalVal * 100) : 0,
      }));
    } else {
      return null;
    }

    if (!sectorExposures || !sectorExposures.length) return null;

    let totalCovered = 0, pos = 0, neu = 0, neg = 0;
    const breakdown = [];

    sectorExposures.forEach(s => {
      const sentiment = sentimentBySector[s.sector];
      if (sentiment === undefined) return;
      totalCovered += s.exposurePct;
      if (sentiment >= 2) pos += s.exposurePct;
      else if (sentiment <= -2) neg += s.exposurePct;
      else neu += s.exposurePct;
      breakdown.push({ sector: s.sector, exposurePct: s.exposurePct, sentiment });
    });

    return { coveredPct: totalCovered, posPct: pos, neuPct: neu, negPct: neg, breakdown };
  }, [source, sectorExposureData, sectorSentiments, sentimentBySector, holdings, tickerAggregates, aggByTicker]);

  const openSectorDetail = (bucket) => {
    if (!sectorExposure?.breakdown) return;
    const rows = [];
    sectorExposure.breakdown.forEach(s => {
      const group = s.sentiment >= 2 ? 'pos' : (s.sentiment <= -2 ? 'neg' : 'neu');
      if (group !== bucket) return;
      rows.push({ sector: s.sector, sentiment: s.sentiment.toFixed(2), exposurePct: s.exposurePct.toFixed(2) + '%', pct: s.exposurePct });
    });
    rows.sort((a, b) => b.pct - a.pct);
    const titleMap = { pos: 'Positive Sectors', neu: 'Neutral Sectors', neg: 'Negative Sectors' };
    setDetailRows(rows);
    setDetailTitle(`TrendSearch Sector Exposure \u2014 ${titleMap[bucket]}`);
    setDetailOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
        <CircularProgress />
        <Typography color="text.secondary" sx={{ ml: 2 }}>Loading TrendSearch data\u2026</Typography>
      </Box>
    );
  }

  const hasValueData = holdings.some(h => h.Value_eur > 0);

  return (
    <Box>
      {/* TrendSearch Exposure */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>TrendSearch Exposure</Typography>
          {trendExposure ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Winners</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={`${trendExposure.winnerPct.toFixed(1)}%`}
                    color="success"
                    size="small"
                    onClick={() => openTickerDetail('winner')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Typography variant="body2" color="text.secondary">of portfolio value</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Losers</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={`${trendExposure.loserPct.toFixed(1)}%`}
                    color="error"
                    size="small"
                    onClick={() => openTickerDetail('loser')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Typography variant="body2" color="text.secondary">of portfolio value</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Enablers/Neutral</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={`${trendExposure.enablerPct.toFixed(1)}%`}
                    color="warning"
                    size="small"
                    onClick={() => openTickerDetail('enabler')}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Typography variant="body2" color="text.secondary">of portfolio value</Typography>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No TrendSearch exposure data available.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Sector Exposure by TrendSearch Sentiment */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Sector Exposure by TrendSearch Sentiment</Typography>
          {sectorExposure ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                TrendSearch covers {sectorExposure.coveredPct.toFixed(1)}% of your portfolio{hasValueData ? ' value' : ''}. Percentages below are of total portfolio.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Positive Sectors</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={`${sectorExposure.posPct.toFixed(1)}%`}
                      color="success"
                      size="small"
                      onClick={() => openSectorDetail('pos')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Typography variant="body2" color="text.secondary">of portfolio value</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Neutral Sectors</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={`${sectorExposure.neuPct.toFixed(1)}%`}
                      color="default"
                      size="small"
                      onClick={() => openSectorDetail('neu')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Typography variant="body2" color="text.secondary">of portfolio value</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Negative Sectors</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={`${sectorExposure.negPct.toFixed(1)}%`}
                      color="error"
                      size="small"
                      onClick={() => openSectorDetail('neg')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Typography variant="body2" color="text.secondary">of portfolio value</Typography>
                  </Box>
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No sector exposure data available.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Detail popup dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{detailTitle}</DialogTitle>
        <DialogContent>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {detailRows.length > 0 && detailRows[0].ticker ? (
                  <>
                    <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sector</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Exposure (EUR)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">% of Portfolio</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 600 }}>Sector</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">TrendSearch Sentiment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">% of Portfolio</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {detailRows.map((r, i) => (
                <TableRow key={i} hover>
                  {r.ticker ? (
                    <>
                      <TableCell>
                        <ClickableTickerChip ticker={r.ticker} source={source} />
                      </TableCell>
                      <TableCell>{r.company}</TableCell>
                      <TableCell>{r.sector}</TableCell>
                      <TableCell align="right">{r.value?.toFixed(2)}</TableCell>
                      <TableCell align="right">{r.pct.toFixed(2)}%</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{r.sector}</TableCell>
                      <TableCell align="right">{r.sentiment}</TableCell>
                      <TableCell align="right">{r.exposurePct}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
              {detailRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No items in this category
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PAModal_NewsTrend;
